import { Box, Button, Grid } from '@mui/material';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import {
  getOrderGuides,
  getOrderGuidesByFacilityAndCategory,
  setOrderGuideItemsAction,
} from '../../../../redux/slices/orderGuides';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import OrderGuideActiveItemsGrid from './OrderGuideActiveItemsGrid';
import OrderGuideCatalogGrid from './OrderGuideCatalogGrid';

export default function OrderGuide() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { id = '' } = useParams();
  const [data, setData] = useState([]);

  const [quickFilter, setQuickFilter] = useState('');
  const [tempOrderGuide, setTempOrderGuide] = useState([]);
  const [active, setActive] = useState(null);
  const [currentOrderGuide, setCurrentOrderGuide] = useState(null);
  const { data: { orderGuideActivations, orderGuide, categories, allProducts, templates } } = useSelector((state) => state.orderGuides);

  useEffect(() => {
    if (currentOrderGuide && active !== null && currentOrderGuide?.active !== active) {
      handleUpdateOrderGuide();
    }
  }, [active, currentOrderGuide?.active]);

  useEffect(() => {
    setCurrentOrderGuide(
      orderGuideActivations.find((orderGuide) => orderGuide.id === parseInt(id, 10)) ||
        templates?.find((template) => template.id === parseInt(id, 10))
    );
  }, [orderGuideActivations, id, templates]);

  useEffect(() => {
    setTempOrderGuide(orderGuide.orderGuide);
    setData(filterForPreferredVendor());
  }, [orderGuide, allProducts]);

  useEffect(() => {
    if (currentOrderGuide) {
      setActive(currentOrderGuide?.active);
    }
  }, [currentOrderGuide, setActive]);

  const filterForPreferredVendor = () => {
    const restrictedLookup = orderGuide.preferences
      .filter((preference) => preference.isRestricted)
      .reduce((acc, curr) => {
        acc[`${curr.vendorId}-${curr.agoraSubcategoryId}`] = curr;
        return acc;
      }, {});

    const preferencesLookup = orderGuide.preferences
      .filter((preference) => !preference.isRestricted)
      .reduce((acc, curr) => {
        acc[`${curr.vendorId}-${curr.agoraSubcategoryId}`] = curr;
        return acc;
      }, {});

    return allProducts.flatMap((product) => {
      const { preferredProducts, productDetails } = product.productDetails.reduce(
        (result, productDetail) => {
          const key = `${productDetail.vendorId}-${product.agoraSubcategory.id}`;
          if (restrictedLookup[key]) return result;
          if (preferencesLookup[key]) result.preferredProducts.push(productDetail);
          else result.productDetails.push(productDetail);
          return result;
        },
        { preferredProducts: [], productDetails: [] }
      );

      if (preferredProducts.length === 0 && productDetails.length === 0) return [];

      const sortedProductDetails = (preferredProducts.length > 0 ? preferredProducts : productDetails).sort(
        (a, b) => a.price / a.uomAmount - b.price / b.uomAmount
      );

      return {
        ...product,
        productDetails: sortedProductDetails,
      };
    });
  };

  useEffect(() => {
    if (!orderGuideActivations?.length) {
      dispatch(getOrderGuides());
    } else {
      dispatch(getOrderGuidesByFacilityAndCategory(id));
    }
  }, [id, orderGuideActivations]);

  const filteredData = useMemo(() => {
    const formattedCategories = categories.map(c => ({
      id: c.value,
      name: c.label,
    }));
    
    const orderGuideLookup =
      tempOrderGuide?.reduce((acc, curr) => {
        acc[`${curr.productId}`] = curr;
        return acc;
      }, {}) || {};
    const detailLookup = data
      .flatMap((product) => [...product.productDetails])
      .reduce((acc, curr) => {
        acc[`${curr.id}`] = curr;
        return acc;
      }, {});
    const updatedData = data.map((item) => {
      const orderGuideItem = orderGuideLookup[item.id];
      const productDetail = orderGuideItem ? detailLookup[orderGuideItem.itemId] : null;
      const priceFromOrderGuide = orderGuideItem?.price;
      const newObj = {
        ...item,
        addedBy: orderGuideItem?.addedBy || null,
        agoraSubcategory: item?.agoraCategory?.id === currentOrderGuide?.agoraCategoryId ? item.agoraSubcategory :
          orderGuide?.subcategories?.find(x => x.id === item?.extraCategories.find(x => x.agoraCategoryId === currentOrderGuide?.agoraCategoryId)?.agoraSubcategoryId)
          ?? item.agoraSubcategory,
        agoraCategory: item?.agoraCategory?.id === currentOrderGuide?.agoraCategoryId ? item.agoraCategory :
          formattedCategories?.find(x => x.id === item?.extraCategories.find(x => x.agoraCategoryId === currentOrderGuide?.agoraCategoryId)?.agoraCategoryId)
          ?? item.agoraCategory,
        activeProductDetail: productDetail
          ? { ...productDetail, price: priceFromOrderGuide ?? productDetail.price }
          : null,
        activeTempOrderGuide: orderGuideItem,
      };
      return newObj;
    });
    const activeData = updatedData.filter(
      (item) => item.activeTempOrderGuide && item.activeTempOrderGuide?.deleteFlag === false
    );
    const unactiveData = updatedData
      .filter((item) => !item.activeProductDetail || item.activeTempOrderGuide.deleteFlag === true)
      .map((item) => {
        const itemProductDetail = item.productDetails.find((pd) => pd.id === item.activeProductDetail?.id) || item.productDetails[0];
        const priceFromOrderGuide = orderGuideLookup[item.id]?.price;
        return { ...item, activeProductDetail: {...itemProductDetail, price: priceFromOrderGuide ?? itemProductDetail.price} };
        });
    return { activeData, unactiveData };
  }, [data, tempOrderGuide, categories, orderGuide.subcategories]);

  const handleUpdateOrderGuide = useCallback(
    async (orderGuide = tempOrderGuide) => {
      try {
        await dispatch(
          setOrderGuideItemsAction(
            id,
            active,
            orderGuide?.filter((item) => item.deleteFlag !== true).map((item) => item.itemId)
          )
        );
        enqueueSnackbar('Order guide updated', { variant: 'success' });
        return true;
      } catch (error) {
        enqueueSnackbar(typeof error === 'string' ? error : 'Unable to update order guide', { variant: 'error' });
        return false;
      }
    },
    [dispatch, id, active, tempOrderGuide, enqueueSnackbar]
  );

  return (
    <>
      <Grid item xs={12}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item>
            <Button
              variant="text"
              onClick={() => {
                navigate(`/dashboard/admin/order-guides`);
              }}
              startIcon={<Iconify icon="mingcute:left-fill" width={24} height={24} sx={{ mx: -1 }} />}
              sx={{
                color: '#454F5B',
                fontWeight: 'bold',
              }}
            >
              Back to order guides
            </Button>
          </Grid>
        </Grid>
        <OrderGuideActiveItemsGrid
          tempOrderGuide={tempOrderGuide}
          setTempOrderGuide={setTempOrderGuide}
          data={filteredData}
          handleUpdateOrderGuide={handleUpdateOrderGuide}
          active={active}
          setActive={setActive}
          currentOrderGuide={currentOrderGuide}
          quickFilter={quickFilter}
          setQuickFilter={setQuickFilter}
        />
        <OrderGuideCatalogGrid
          currentOrderGuide={currentOrderGuide}
          data={filteredData}
          tempOrderGuide={tempOrderGuide}
          setTempOrderGuide={setTempOrderGuide}
          handleUpdateOrderGuide={handleUpdateOrderGuide}
          quickFilter={quickFilter}
          subcategories={orderGuide?.subcategories || []}
        />
      </Grid>
    </>
  );
}
